import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, DollarSign, Mail, Phone, User, Home } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { getBooking } from '../../services/bookings';
import './Confirmation.css';

const Confirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBooking();
  }, [bookingId]);

  const loadBooking = async () => {
    setLoading(true);
    const result = await getBooking(bookingId);
    
    if (result.success) {
      setBooking(result.data);
    } else {
      setError('Booking not found');
    }
    
    setLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="public-confirmation-page">
        <PublicNavbar />
        <div className="page-content">
          <div className="container">
            <LoadingSpinner fullScreen />
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="public-confirmation-page">
        <PublicNavbar />
        <div className="page-content">
          <div className="container">
            <div className="confirmation-error">
              <h2>Booking Not Found</h2>
              <p>We couldn't find the booking you're looking for.</p>
              <Link to="/" className="btn btn-primary">
                <Home size={20} />
                Go Home
              </Link>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="public-confirmation-page">
      <PublicNavbar />

      <div className="page-content">
        <div className="container">
          <div className="confirmation-container">
            {/* Success Header */}
            <div className="confirmation-header">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h1 className="confirmation-title">Booking Confirmed!</h1>
              <p className="confirmation-subtitle">
                Thank you for your booking. A confirmation email has been sent to {booking.customerEmail}
              </p>
              <div className="booking-id">
                <span className="booking-id-label">Booking ID:</span>
                <span className="booking-id-value">{bookingId}</span>
              </div>
            </div>

            {/* Booking Details */}
            <div className="confirmation-details">
              <div className="details-section">
                <h2 className="details-title">Contact Information</h2>
                <div className="details-grid">
                  <div className="detail-item">
                    <User size={20} />
                    <div className="detail-content">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">{booking.customerName}</span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <Mail size={20} />
                    <div className="detail-content">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{booking.customerEmail}</span>
                    </div>
                  </div>

                  {booking.customerPhone && (
                    <div className="detail-item">
                      <Phone size={20} />
                      <div className="detail-content">
                        <span className="detail-label">Phone</span>
                        <span className="detail-value">{booking.customerPhone}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Booked Items */}
              <div className="details-section">
                <h2 className="details-title">Booked Items</h2>
                <div className="booked-items">
                  {booking.items.map((item, index) => (
                    <div key={index} className="booked-item">
                      <div className="booked-item-header">
                        <div className="booked-item-type">{item.type}</div>
                        <div className="booked-item-price">${item.total.toFixed(2)}</div>
                      </div>
                      <h3 className="booked-item-name">{item.name}</h3>
                      <div className="booked-item-dates">
                        <Calendar size={16} />
                        <span>
                          {formatDate(item.startDate)} - {formatDate(item.endDate)}
                        </span>
                        <span className="booked-item-days">({item.days} days)</span>
                      </div>
                      <div className="booked-item-breakdown">
                        <span>${item.price}/day × {item.days} days</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {booking.notes && (
                <div className="details-section">
                  <h2 className="details-title">Additional Notes</h2>
                  <div className="notes-box">
                    <p>{booking.notes}</p>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="details-section payment-summary">
                <h2 className="details-title">Payment Summary</h2>
                <div className="payment-details">
                  <div className="payment-row">
                    <span className="payment-label">Subtotal</span>
                    <span className="payment-value">${booking.totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="payment-row payment-total">
                    <span className="payment-label">Total Paid</span>
                    <span className="payment-value">${booking.totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="payment-status">
                    <CheckCircle size={16} />
                    <span>Payment Confirmed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="confirmation-next-steps">
              <h2 className="next-steps-title">What's Next?</h2>
              <div className="next-steps-grid">
                <div className="next-step">
                  <div className="next-step-number">1</div>
                  <div className="next-step-content">
                    <h3>Check Your Email</h3>
                    <p>You'll receive a confirmation email with all booking details.</p>
                  </div>
                </div>
                <div className="next-step">
                  <div className="next-step-number">2</div>
                  <div className="next-step-content">
                    <h3>We'll Contact You</h3>
                    <p>Our team will reach out to finalize arrangements.</p>
                  </div>
                </div>
                <div className="next-step">
                  <div className="next-step-number">3</div>
                  <div className="next-step-content">
                    <h3>Enjoy Your Booking</h3>
                    <p>Show up on your scheduled date and we'll take care of the rest.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="confirmation-actions">
              <Link to="/" className="btn btn-primary btn-lg">
                <Home size={20} />
                Back to Home
              </Link>
              <button 
                onClick={() => window.print()} 
                className="btn btn-secondary btn-lg"
              >
                Print Confirmation
              </button>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Confirmation;